import { jsPDF } from 'jspdf';
import {
  CORPORATE_RGB,
  HEADER_HEIGHT_MM,
  LOGO_BLANCO_SRC,
  dateStamp,
  drawPdfBrandedHeader,
  loadImageElement,
  safeFilenamePart,
} from './exportListadoBranded';
import {
  buildActividadesPorFecha,
  getCalendarioFiltroLabel,
} from './calendarioFiltros';
import {
  buildExportMonthGrid,
  drawMonthCalendarGrid,
  estimateMonthGridHeight,
} from './calendarioExportGrid';

const CADENCYA_DEEP = '#4C1D95';

async function loadCadencyaLogoAsset() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  try {
    const res = await fetch(`${origin}${encodeURI(LOGO_BLANCO_SRC)}`);
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const img = await loadImageElement(dataUrl);
    return { dataUrl, format: 'PNG', width: img.naturalWidth || 1, height: img.naturalHeight || 1, img };
  } catch {
    try {
      const img = await loadImageElement(LOGO_BLANCO_SRC);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      return {
        dataUrl: canvas.toDataURL('image/png'),
        format: 'PNG',
        width: img.naturalWidth || 1,
        height: img.naturalHeight || 1,
        img,
      };
    } catch {
      return null;
    }
  }
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function drawPngHeader(ctx, width, headerH, { bandName, filtroLabel, monthTitle, logoImg, bandLogoImg }) {
  ctx.fillStyle = `rgb(${CORPORATE_RGB.join(',')})`;
  ctx.fillRect(0, 0, width, headerH);

  if (logoImg) {
    const lw = logoImg.naturalWidth || logoImg.width;
    const lh = logoImg.naturalHeight || logoImg.height;
    const maxW = width * 0.28;
    const maxH = headerH * 0.55;
    const scale = Math.min(maxW / lw, maxH / lh, 1);
    const dw = lw * scale;
    const dh = lh * scale;
    ctx.drawImage(logoImg, 36, (headerH - dh) / 2 - 8, dw, dh);
  }

  if (bandLogoImg) {
    const lw = bandLogoImg.naturalWidth || bandLogoImg.width;
    const lh = bandLogoImg.naturalHeight || bandLogoImg.height;
    const maxW = width * 0.22;
    const maxH = headerH * 0.5;
    const scale = Math.min(maxW / lw, maxH / lh, 1);
    const dw = lw * scale;
    const dh = lh * scale;
    ctx.drawImage(bandLogoImg, (width - dw) / 2, (headerH - dh) / 2 - 6, dw, dh);
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.font = '700 28px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  ctx.fillText('Calendario', width - 36, 24);
  ctx.font = '500 18px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  if (bandName) ctx.fillText(String(bandName), width - 36, 58);
  ctx.font = '400 14px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillText(filtroLabel, width - 36, 82);
  if (monthTitle) {
    ctx.fillText(monthTitle, width - 36, 100);
  }
}

function drawPngFooter(ctx, width, height, footerH, logoImg) {
  const fy = height - footerH;
  const grad = ctx.createLinearGradient(0, fy, 0, height);
  grad.addColorStop(0, '#5B21B6');
  grad.addColorStop(0.5, CADENCYA_DEEP);
  grad.addColorStop(1, '#2E1065');
  ctx.fillStyle = grad;
  ctx.fillRect(0, fy, width, footerH);

  if (logoImg) {
    const lw = logoImg.naturalWidth || logoImg.width;
    const lh = logoImg.naturalHeight || logoImg.height;
    const maxW = width * 0.42;
    const maxH = footerH * 0.45;
    const scale = Math.min(maxW / lw, maxH / lh, 1);
    const dw = lw * scale;
    const dh = lh * scale;
    ctx.drawImage(logoImg, (width - dw) / 2, fy + 14, dw, dh);
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '800 22px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Cadencya', width / 2, fy + 28);
  }

  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = '600 14px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('cadencya.com', width / 2, height - 18);
}

function drawLegend(ctx, x, y, width, showActividades) {
  const items = [
    { color: '#C6F6D5', border: '#68D391', label: 'Disponible' },
    { color: '#FED7D7', border: '#FC8181', label: 'Ocupado' },
  ];
  if (showActividades) {
    items.push(
      { color: '#BEE3F8', border: '#3182CE', label: 'Actuación (acuerdo)' },
      { color: '#FEEBC8', border: '#DD6B20', label: 'Actuación' },
      { color: '#FED7E2', border: '#D53F8C', label: 'Ensayo' },
    );
  }

  let cx = x;
  ctx.font = '600 10px ui-sans-serif, system-ui, "Segoe UI", sans-serif';
  ctx.textBaseline = 'middle';

  for (const item of items) {
    const box = 12;
    ctx.fillStyle = item.color;
    ctx.strokeStyle = item.border;
    ctx.lineWidth = 1;
    ctx.fillRect(cx, y, box, box);
    ctx.strokeRect(cx, y, box, box);
    ctx.fillStyle = '#4A5568';
    ctx.textAlign = 'left';
    ctx.fillText(item.label, cx + box + 6, y + box / 2);
    cx += box + 6 + ctx.measureText(item.label).width + 16;
    if (cx > x + width - 80) break;
  }

  return 24;
}

async function renderCalendarioCanvas({
  currentMonth,
  calendarData,
  actuaciones,
  ensayos,
  filtro,
  bandName,
  bandLogoAsset = null,
  includeBranding = true,
}) {
  const showActividades = filtro !== 'nada';
  const actividadesPorFecha = buildActividadesPorFecha(actuaciones, ensayos);
  const grid = buildExportMonthGrid({
    currentMonth,
    calendarData,
    actividadesPorFecha,
    showActividades,
  });

  const width = 1200;
  const pad = 36;
  const headerH = includeBranding ? 120 : 0;
  const footerH = includeBranding ? 96 : 0;
  const legendH = showActividades ? 28 : 24;
  const gridH = estimateMonthGridHeight();
  const height = headerH + pad + legendH + gridH + pad + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas no disponible');

  ctx.fillStyle = '#FAF5FF';
  ctx.fillRect(0, 0, width, height);

  const logoAsset = await loadCadencyaLogoAsset();
  const logoImg = logoAsset?.img || (logoAsset?.dataUrl
    ? await loadImageElement(logoAsset.dataUrl)
    : null);

  let bandLogoImg = null;
  if (bandLogoAsset?.dataUrl) {
    try {
      bandLogoImg = await loadImageElement(bandLogoAsset.dataUrl);
    } catch {
      bandLogoImg = null;
    }
  }

  const filtroLabel = getCalendarioFiltroLabel(filtro);

  if (includeBranding) {
    drawPngHeader(ctx, width, headerH, {
      bandName,
      filtroLabel,
      monthTitle: grid.monthTitle,
      logoImg,
      bandLogoImg,
    });
  }

  let y = headerH + (includeBranding ? pad : 16);
  drawLegend(ctx, pad, y, width - pad * 2, showActividades);
  y += legendH;

  drawMonthCalendarGrid(ctx, pad, y, width - pad * 2, grid);
  if (includeBranding) {
    drawPngFooter(ctx, width, height, footerH, logoImg);
  }

  return { canvas, grid, filtroLabel };
}

/**
 * @param {{
 *   currentMonth: Date,
 *   calendarData: object,
 *   actuaciones: object[],
 *   ensayos: object[],
 *   bandName?: string,
 *   filtro?: string,
 *   bandLogoAsset?: object | null,
 * }} params
 */
export async function downloadCalendarioPdf({
  currentMonth,
  calendarData,
  actuaciones,
  ensayos,
  bandName,
  filtro = 'todo',
  bandLogoAsset = null,
}) {
  const { canvas, grid, filtroLabel } = await renderCalendarioCanvas({
    currentMonth,
    calendarData,
    actuaciones,
    ensayos,
    filtro,
    bandName,
    bandLogoAsset,
    includeBranding: false,
  });

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const headerH = HEADER_HEIGHT_MM;
  const marginX = 10;
  const contentTop = headerH + 4;
  const contentW = pageW - marginX * 2;
  const contentH = pageH - contentTop - 8;

  const cadencyaLogoAsset = await loadCadencyaLogoAsset();
  let bandLogoForHeader = bandLogoAsset?.dataUrl ? bandLogoAsset : null;

  const subtitle = `${grid.monthTitle} · ${filtroLabel}`;
  drawPdfBrandedHeader(doc, {
    listTitle: 'Calendario',
    bandName,
    subtitle,
    logoAsset: cadencyaLogoAsset,
    bandLogoAsset: bandLogoForHeader,
    headerHeightMm: headerH,
  });

  const imgData = canvas.toDataURL('image/png');
  const imgRatio = canvas.width / canvas.height;
  let drawW = contentW;
  let drawH = drawW / imgRatio;
  if (drawH > contentH) {
    drawH = contentH;
    drawW = drawH * imgRatio;
  }
  const drawX = marginX + (contentW - drawW) / 2;
  doc.addImage(imgData, 'PNG', drawX, contentTop, drawW, drawH);

  const outName = `calendario_${safeFilenamePart(bandName)}_${dateStamp()}.pdf`;
  doc.save(outName);
}

/**
 * @param {{
 *   currentMonth: Date,
 *   calendarData: object,
 *   actuaciones: object[],
 *   ensayos: object[],
 *   bandName?: string,
 *   filtro?: string,
 *   bandLogoAsset?: object | null,
 * }} params
 */
export async function downloadCalendarioPng({
  currentMonth,
  calendarData,
  actuaciones,
  ensayos,
  bandName,
  filtro = 'todo',
  bandLogoAsset = null,
}) {
  const { canvas } = await renderCalendarioCanvas({
    currentMonth,
    calendarData,
    actuaciones,
    ensayos,
    filtro,
    bandName,
    bandLogoAsset,
    includeBranding: true,
  });

  const outName = `calendario_${safeFilenamePart(bandName)}_${dateStamp()}.png`;
  await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('No se pudo generar la imagen'));
        return;
      }
      triggerBlobDownload(blob, outName);
      resolve();
    }, 'image/png');
  });
}

export async function loadBandaLogoAssetForCalendario() {
  try {
    const res = await fetch('/api/bandas/me/avatar-file', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
      },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.size) return null;
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const img = await loadImageElement(dataUrl);
    return {
      dataUrl,
      format: 'PNG',
      width: img.naturalWidth || 1,
      height: img.naturalHeight || 1,
      img,
    };
  } catch {
    return null;
  }
}
